<template>
  <DialogLayout :class-name="'create-account-dialog'" @close="handleClose">
    <template #title>{{ title || (isEditMode ? t('account.createDialog.editTitle') : t('account.createDialog.title')) }}</template>
    <template #default>
      <el-form label-position="top" :model="formData" :rules="rules" ref="formRef">
        <el-form-item :label="t('account.createDialog.email')" prop="email">
          <el-input
            v-model="formData.email"
            :placeholder="t('account.createDialog.emailPlaceholder')"
            :disabled="isEditMode"
          />
        </el-form-item>

        <!-- 编辑模式下直接显示所有表单项，创建模式下邮箱验证通过后显示 -->
        <template v-if="isEditMode || emailValidated">
          <el-form-item :label="t('account.createDialog.username')" prop="username" required>
            <el-input
              v-model="formData.username"
              :placeholder="t('account.createDialog.usernamePlaceholder')"
            />
          </el-form-item>

          <!-- Career角色（权限） -->
          <el-form-item :class="{ 'no-permission-hint': !careerProductPurchased }" prop="selectedRole">
            <template #label>
              <div class="role-label">
                <el-checkbox
                  v-model="careerRoleEnabled"
                  class="role-checkbox"
                  @click.stop
                  :disabled="!careerProductPurchased"
                >
                  <span>{{ t('account.createDialog.careerRoleName') }}</span>
                  <span v-if="!isEditMode && !talentRoleEnabled && !careerRoleEnabled" class="required-asterisk">*</span>
                  <span v-else-if="!isEditMode && careerRoleEnabled" class="required-asterisk">*</span>
                </el-checkbox>
              </div>
            </template>
            <template v-if="careerProductPurchased">
              <el-select
                v-model="formData.careerRoleName"
                :placeholder="t('account.createDialog.roleSelectPlaceholder')"
                :disabled="!careerRoleEnabled"
                class="role-select"
                v-if="careerRoleEnabled"
              >
                <el-option
                  v-for="role in careerRoles"
                  :key="role.value"
                  :label="role.label"
                  :value="role.value"
                />
              </el-select>
              <div class="no-role-permission-hint" v-else>
                <span class="hint-text">{{ t('account.noRolePermission', { roleType: t('account.createDialog.careerRole') }) }}</span>
              </div>
            </template>
            <template v-else>
              <el-icon
                :size="22"
                color="#E6A23C"
                style="cursor: pointer; margin-right: 5.33px;"
              >
                <WarnTriangleFilled />
              </el-icon>
              <span>{{ t('account.notPurchased') }}</span>
            </template>
          </el-form-item>

          <!-- Talent角色（权限） -->
          <el-form-item :class="{ 'no-permission-hint': !talentProductPurchased }" prop="selectedRole">
            <template #label>
              <div class="role-label">
                <el-checkbox
                  v-model="talentRoleEnabled"
                  class="role-checkbox"
                  @click.stop
                  :disabled="!talentProductPurchased"
                >
                  <span>{{ t('account.createDialog.talentRoleName') }}</span>
                  <span v-if="!isEditMode && !talentRoleEnabled && !careerRoleEnabled" class="required-asterisk">*</span>
                  <span v-else-if="!isEditMode && talentRoleEnabled" class="required-asterisk">*</span>
                </el-checkbox>
              </div>
            </template>
            <template v-if="talentProductPurchased">
              <el-select
                v-model="formData.talentRoleName"
                :placeholder="t('account.createDialog.roleSelectPlaceholder')"
                :disabled="!talentRoleEnabled"
                class="role-select"
                v-if="talentRoleEnabled"
              >
                <el-option
                  v-for="role in talentRoles"
                  :key="role.value"
                  :label="role.label"
                  :value="role.value"
                />
              </el-select>
              <div class="no-role-permission-hint" v-else>
                <span class="hint-text">{{ t('account.noRolePermission', { roleType: t('account.createDialog.talentRole') }) }}</span>
              </div>
            </template>
            <template v-else>
              <el-icon
                :size="22"
                color="#E6A23C"
                style="cursor: pointer; margin-right: 5.33px;"
              >
                <WarnTriangleFilled />
              </el-icon>
              <span>{{ t('account.notPurchased') }}</span>
            </template>
          </el-form-item>
        </template>
      </el-form>
    </template>
    <template #footer>
      <el-button type="default" @click="handleCancel">{{ t('account.createDialog.cancel') }}</el-button>
      <el-button type="primary" @click="handleConfirm" :disabled="!isFormValid">
        {{ isEditMode ? t('account.createDialog.save') : t('account.createDialog.create') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FormInstance, FormRules } from 'element-plus';
import { WarnTriangleFilled } from '@element-plus/icons-vue'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import { checkEmailExists,  getAccountById, getRoleListForSuperAdmin} from '@/api/account'
import { ElMessage } from 'element-plus';
import {
  RoleLocalesMap
} from '@/router/config/roles'
import { ProductId } from '@/router/config/roles';
const { t } = useI18n();

interface Props {
  title?: string;
  username?: string;
  email?: string;
  position?: string;
  careerRoleName?: string;
  talentRoleName?: string;
  id?: string | number; // 用于判断是否为编辑模式
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  username: '',
  email: '',
  position: '',
  careerRoleName: undefined,
  talentRoleName: undefined,
  id: undefined
});

const emit = defineEmits<{
  cancel: [];
  confirm: [data: any];
  close: [];
}>();

const formRef = ref<FormInstance>();

// 判断是否为编辑模式
const isEditMode = computed(() => !!props.id);

// 邮箱验证状态 - 编辑模式下默认为已验证
const emailValidated = computed(() => isEditMode.value || emailValidatedTemp.value);
const emailValidatedTemp = ref(false);

// 表单是否有效的计算属性
const isFormValid = computed(() => {
  if (!formData.email || !formData.username) return false;
  if (!isEditMode.value && !emailValidated.value) return false;
  // 在新增模式下，至少选择一个角色；在编辑模式下，允许不选择任何角色
  if (!isEditMode.value && !careerRoleEnabled.value && !talentRoleEnabled.value) return false;
  // 如果启用了角色，必须选择具体的角色值
  if (!isEditMode.value) {
    const hasValidCareer = careerRoleEnabled.value && formData.careerRoleName;
    const hasValidTalent = talentRoleEnabled.value && formData.talentRoleName;

    // 只有当两个角色都不具备"有效值"时，才返回 false
    if (!hasValidCareer && !hasValidTalent) return false;
  }
  return true;
});

// 角色启用状态
const careerRoleEnabled = ref(false);
const talentRoleEnabled = ref(false);

// 监听角色状态变化，触发表单验证
watch([careerRoleEnabled, talentRoleEnabled], async () => {
  if (formRef.value) {
    // 验证selectedRole字段
    try {
      await formRef.value.validateField('selectedRole');
    } catch (error) {
      // 验证失败时，如果至少选择了一个角色，则清除验证错误
      if (careerRoleEnabled.value || talentRoleEnabled.value) {
        await formRef.value.clearValidate(['selectedRole']);
      }
    }
  }
});

// 角色列表数据
const roleListData = ref<any[]>([]);
const talentRoles = ref<{ id: number; value: string; label: string }[]>([]);
const careerRoles = ref<{ id: number; value: string; label: string }[]>([]);

// 产品购买状态
const careerProductPurchased = ref(true);
const talentProductPurchased = ref(true);

// 获取角色列表
const fetchRoleList = async () => {
  try {
    const resp_data = await getRoleListForSuperAdmin();
    if (resp_data) {
      roleListData.value = resp_data;
      // 处理返回的数据，提取Talent和Career角色
      resp_data.forEach((item: any) => {
        if (item.productId === ProductId.TALENT) {
          talentProductPurchased.value = item.enterprisePurchased;
          if (item.enterprisePurchased) {
            talentRoles.value = item.roles.map((role: any) => ({
              id: role.id,
              value: role.name,
              // label: t(`account.roles.${getRoleKeyByValue(role.name)}`)
              label: t(RoleLocalesMap[role.name])
            }));
          }
        } else if (item.productId === ProductId.ATS) {
          careerProductPurchased.value = item.enterprisePurchased;
          if (item.enterprisePurchased) {
            careerRoles.value = item.roles.map((role: any) => ({
              id: role.id,
              value: role.name,
              label: t(RoleLocalesMap[role.name])
            }));
          }
        }
      });
    }
  } catch (error) {
    console.error('获取角色列表失败:', error);
    ElMessage.error(t('account.createDialog.fetchRolesError'));
  }
};
const formData = reactive({
  id: props.id,
  username: props.username,
  email: props.email,
  position: '',
  careerRoleName: props.careerRoleName as string | undefined,
  talentRoleName: props.talentRoleName as string | undefined
});

// 当编辑模式时，获取账号详情并设置表单状态
watch(() => props.id, async (id) => {
  if (id && isEditMode.value) {
    try {
      const accountDetail = await getAccountById(id as number);
      if (accountDetail) {
        const data = accountDetail;
        // 设置基础信息
        formData.username = data.name;
        formData.email = data.email;
        formData.position = data.position || '';

        // 初始化角色状态
        careerRoleEnabled.value = false;
        talentRoleEnabled.value = false;

        // 根据roles数组设置角色状态
        if (data.roles && Array.isArray(data.roles)) {
          data.roles.forEach((role: any) => {
            if (role.productId === 1) { // Talent角色
              talentRoleEnabled.value = true;
              formData.talentRoleName = role.name;
            } else if (role.productId === 2) { // Career角色
              careerRoleEnabled.value = true;
              formData.careerRoleName = role.name;
            }
          });
        }

        emailValidatedTemp.value = true; // 编辑模式下邮箱已验证
      }
    } catch (error) {
      console.error('获取账号详情失败:', error);
      ElMessage.error(t('account.createDialog.fetchDetailError'));
    }
  }
}, { immediate: true });

// 邮箱存在校验（防抖处理，延迟300ms避免频繁请求）
let checkEmailTimeout: ReturnType<typeof setTimeout> | null = null;

const debouncedCheckEmail = (value: string): Promise<any> => {
  return new Promise((resolve) => {
    if (checkEmailTimeout) {
      clearTimeout(checkEmailTimeout);
    }
    checkEmailTimeout = setTimeout(() => {
      checkEmailExists(value)
        .then(resolve)
        .catch(() => {
          resolve({ status: -1 });
        });
    }, 300);
  });
};

const rules: FormRules = {
  email: [
    {
      validator: (rule: any, value: any, callback: any) => {
        if (!value) {
          callback(new Error(t('account.createDialog.validation.emailRequired')));
          emailValidatedTemp.value = false;
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          callback(new Error(t('account.createDialog.validation.emailInvalid')));
          emailValidatedTemp.value = false;
          return;
        }

        if (isEditMode.value) {
          emailValidatedTemp.value = true;
          callback();
          return;
        }

        debouncedCheckEmail(value)
          .then((emailCheckResult: any) => {
            if (emailCheckResult.status === 1) {
              callback(new Error(t('account.createDialog.validation.emailExists')));
              emailValidatedTemp.value = false;
            } else if (emailCheckResult.status === 2) {
              callback(new Error(t('account.createDialog.validation.emailExistsInOtherCompany')));
              emailValidatedTemp.value = false;
            } else if (emailCheckResult.status === 0) {
              emailValidatedTemp.value = true;
              callback();
            } else {
              callback(new Error(t('account.createDialog.validation.emailInvalid')));
              emailValidatedTemp.value = false;
            }
          })
          .catch(() => {
            callback(new Error(t('account.createDialog.validation.emailInvalid')));
            emailValidatedTemp.value = false;
          });
      },
      trigger: 'change'
    }
  ],
  username: [
    { required: true, message: t('account.createDialog.validation.usernameRequired'), trigger: 'blur' }
  ],
  selectedRole: [
    {
      validator: (rule: any, value: any, callback: any) => {
        if (!isEditMode.value && !careerRoleEnabled.value && !talentRoleEnabled.value) {
          callback(new Error(t('account.createDialog.validation.atLeastOneRole')));
        } else {
          callback();
        }
      },
      trigger: 'change'
    }
  ]
};

// 移除旧的 validateEmail 函数（已迁移到 rules 中）
// const validateEmail = async () => { ... }

const handleCancel = () => {
  emit('cancel');
};

const handleConfirm = async () => {
  if (!formRef.value) return;

  // 验证表单
  await formRef.value.validate(async (valid) => {
    if (valid) {
      // 根据复选框状态确定角色值，提取 productId 和 roleId
      const productRoleRelations = [];

      // 添加 Career 角色（如果启用且产品已购买）
      if (careerRoleEnabled.value && careerProductPurchased.value && formData.careerRoleName) {
        const selectedCareerRole = careerRoles.value.find(role => role.value === formData.careerRoleName);
        if (selectedCareerRole) {
          productRoleRelations.push({
            productId: 2, // Career 对应 productId 2
            roleId: selectedCareerRole.id
          });
        }
      }

      // 添加 Talent 角色（如果启用且产品已购买）
      if (talentRoleEnabled.value && talentProductPurchased.value && formData.talentRoleName) {
        const selectedTalentRole = talentRoles.value.find(role => role.value === formData.talentRoleName);
        if (selectedTalentRole) {
          productRoleRelations.push({
            productId: 1, // Talent 对应 productId 1
            roleId: selectedTalentRole.id
          });
        }
      }

      // 准备发送到后端的数据
      const requestData = {
        id: formData.id || 0,
        email: formData.email,
        name: formData.username,
        position: formData.position || '', // 如果需要，可以从表单中添加职位字段
        productRoleRelations
      };

      // 将数据通过emit传递给父组件处理
      emit('confirm', requestData);
    }
  });
};

// 监听ESC按键关闭弹窗
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    handleClose();
  }
};

// 组件挂载时获取角色列表并添加键盘事件监听器
onMounted(async () => {
  await fetchRoleList(); // 获取角色列表
  document.addEventListener('keydown', handleKeydown);
});

// 组件卸载时移除键盘事件监听器
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

const handleClose = () => {
  emit('close');
};
</script>

<style lang="scss" scoped>
.create-account-dialog {
  min-width: 592px;
  max-width: 592px;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.no-permission-hint {
  margin-top: -10px;
  font-size: 14px;
  color: #F49119;
  line-height: 20px;
  background: #F7F8FA;
  padding: 8px;
  border-radius: 4px;
}

.no-role-permission-hint {
  margin-top: -10px;
  font-size: 14px;
  color: #88929B;
  line-height: 20px;
  padding: 8px 0;
}



.role-section {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.role-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.role-checkbox {
  flex: 0 0 auto;
  margin-right: 0;
}

.role-select {
  flex: 1;
}

.required-asterisk {
  color: #EF3D3F;
  margin-left: 2px;
  font-size: 110%;
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

:deep(.el-form-item__label) {
  font-size: 14px;
  color: #262626;
  font-weight: 500;
  margin-bottom: 8px;
}

:deep(.el-input__inner) {
  height: 35px;
}

:deep(.el-radio-group) {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

:deep(.el-select .el-input__wrapper) {
  padding: 0 12px;
}
</style>